import { getDefaultStore } from "jotai";
import {
  sideBarWidthAtom,
  iframeFocusModeSetupSetter,
} from "@zapier/canvas-state";

/**
 * Represents a cached iframe entry with metadata for lifecycle management
 */
interface CacheEntry {
  iframe: HTMLIFrameElement;
  timestamp: number;
  abortController: AbortController;
  isReady: boolean;
  isCleanedUp: boolean;
}

/**
 * Manages iframe preloading for focus mode performance.
 * Maintains LRU cache of up to 5 preloaded iframes to reduce loading delays.
 */
class PreloadService {
  private cache = new Map<string, CacheEntry>();
  private store = getDefaultStore();

  // Balance between performance and memory usage for typical workflows
  private readonly maxSize = 5;

  // Animation constants
  private readonly HEADER_HEIGHT = 85;
  private readonly ANIMATION_DURATION = "0.4s";
  private readonly ANIMATION_EASING = "cubic-bezier(0.4, 0, 0.2, 1)";
  private readonly Z_INDEX_MODAL = "1000";
  private readonly Z_INDEX_HIDDEN = "-1";

  /**
   * Gets current sidebar width for iframe positioning
   */
  private getCurrentSideBarWidth(): number {
    return this.store.get(sideBarWidthAtom);
  }

  /**
   * Preload iframe for given URL. No-op if already cached.
   */
  preload(url: string): void {
    if (this.cache.has(url)) {
      return;
    }

    // Enforce cache size limit before adding new entry
    this.enforceCacheSize();

    // Create new iframe with memory leak prevention
    const abortController = new AbortController();
    const iframe = this.createIframe(url, abortController);

    // Add to cache
    this.cache.set(url, {
      iframe,
      timestamp: Date.now(),
      abortController,
      isReady: false,
      isCleanedUp: false,
    });

    // Add to DOM immediately but keep hidden
    document.body.appendChild(iframe);
  }

  /**
   * Retrieve and position preloaded iframe for display.
   * Returns null if not cached or cleaned up.
   */
  take(
    url: string,
    currentSideBarWidth?: number,
    originPosition?: { x: number; y: number } | null,
  ): { iframe: HTMLIFrameElement; isReady: boolean } | null {
    const entry = this.cache.get(url);
    if (!entry || entry.isCleanedUp) {
      return null;
    }

    // Position iframe for modal display
    this.positionIframeForModal(
      entry.iframe,
      currentSideBarWidth,
      originPosition,
    );

    return {
      iframe: entry.iframe,
      isReady: entry.isReady,
    };
  }

  /**
   * Check if URL is preloaded and ready for immediate use
   */
  isPreloadedAndReady(url: string): boolean {
    const entry = this.cache.get(url);
    return entry && !entry.isCleanedUp ? entry.isReady : false;
  }

  /**
   * Clean up all cached iframes and abort ongoing loads
   */
  cleanup(): void {
    for (const [, entry] of this.cache) {
      this.cleanupEntry(entry);
    }
    this.cache.clear();
  }

  /**
   * Dispose of service and prevent memory leaks
   */
  dispose(): void {
    this.cleanup();
  }

  /**
   * Remove oldest entry when at max cache size (LRU eviction)
   */
  private enforceCacheSize(): void {
    if (this.cache.size >= this.maxSize) {
      let oldestUrl = "";
      let oldestTimestamp = Date.now();

      // Find the oldest entry by timestamp
      for (const [url, entry] of this.cache) {
        if (entry.timestamp < oldestTimestamp) {
          oldestTimestamp = entry.timestamp;
          oldestUrl = url;
        }
      }

      // Clean up and remove the oldest entry
      if (oldestUrl) {
        const entry = this.cache.get(oldestUrl)!;
        this.cleanupEntry(entry);
        this.cache.delete(oldestUrl);
      }
    }
  }

  /**
   * Clean up cache entry and remove iframe from DOM
   */
  private cleanupEntry(entry: CacheEntry): void {
    // Mark as cleaned up first to prevent race conditions
    entry.isCleanedUp = true;

    // Abort any ongoing operations
    entry.abortController.abort();

    // Remove from DOM
    if (entry.iframe.parentNode) {
      entry.iframe.remove();
    }
  }

  /**
   * Mark iframe as ready when loading completes
   */
  private markIframeAsReady(iframe: HTMLIFrameElement): void {
    const entry = Array.from(this.cache.values()).find(
      (entry) => entry.iframe === iframe,
    );

    // Prevent race condition: don't update if already cleaned up
    if (entry && !entry.isCleanedUp) {
      entry.isReady = true;
    }
  }

  /**
   * Create hidden iframe with event listeners and Canvas integration
   */
  private createIframe(
    url: string,
    abortController: AbortController,
  ): HTMLIFrameElement {
    const iframe = document.createElement("iframe");
    const currentSideBarWidth = this.getCurrentSideBarWidth();

    // Set iframe attributes
    iframe.src = url;
    iframe.dataset.preloadUrl = url;
    iframe.dataset.preloadStatus = "loading";

    // Position iframe hidden off-screen during preload
    const cssString = `
      position: fixed;
      top: ${this.HEADER_HEIGHT}px;
      left: ${currentSideBarWidth}px;
      width: calc(100vw - ${currentSideBarWidth}px);
      height: 100vh;
      border: none;
      z-index: ${this.Z_INDEX_HIDDEN};
      opacity: 0;
      pointer-events: none;
      transform: scale(0);
      transform-origin: center center;
    `;

    iframe.style.cssText = cssString;

    const handleLoad = () => {
      if (abortController.signal.aborted) return;

      iframe.dataset.preloadStatus = "loaded";
      this.markIframeAsReady(iframe);
    };

    iframe.addEventListener("load", handleLoad, {
      signal: abortController.signal,
    });

    // Set up Canvas iframe communication
    this.store.set(iframeFocusModeSetupSetter, {
      iframe,
      onReady: () => {
        this.markIframeAsReady(iframe);
      },
    });

    return iframe;
  }

  /**
   * Position iframe for modal display (visible and interactive)
   */
  private positionIframeForModal(
    iframe: HTMLIFrameElement,
    providedSideBarWidth?: number,
    originPosition?: { x: number; y: number } | null,
  ): void {
    const currentSideBarWidth =
      providedSideBarWidth ?? this.getCurrentSideBarWidth();

    // Check if iframe is already visible to avoid re-animating
    const isIframeAlreadyVisible =
      iframe.style.zIndex === this.Z_INDEX_MODAL &&
      iframe.style.opacity === "1";

    // Set consistent positioning and transform origin
    const transformOrigin = originPosition
      ? `${originPosition.x}px ${originPosition.y}px`
      : "center center";

    // Apply base modal positioning
    const baseModalCss = `
      position: fixed;
      left: ${currentSideBarWidth}px;
      top: ${this.HEADER_HEIGHT}px;
      width: calc(100vw - ${currentSideBarWidth}px);
      height: calc(100vh - ${this.HEADER_HEIGHT}px);
      border: none;
      z-index: ${this.Z_INDEX_MODAL};
      pointer-events: auto;
      backface-visibility: hidden;
      transform-origin: ${transformOrigin};
    `;

    iframe.style.cssText = baseModalCss;

    if (isIframeAlreadyVisible) {
      // Already visible - just ensure final state without animation
      const iframeAlreadyVisibleCss = `
        ${iframe.style.cssText}
        opacity: 1;
        transform: scale3d(1, 1, 1);
        transition: none;
      `;
      iframe.style.cssText = iframeAlreadyVisibleCss;
      return;
    }

    // First time showing - set up scale animation
    const animationSetupCss = `
      ${iframe.style.cssText}
      opacity: 0;
      transform: scale3d(0, 0, 1);
      transition: none;
      will-change: transform, opacity;
    `;
    iframe.style.cssText = animationSetupCss;

    // Trigger animation on next frame
    requestAnimationFrame(() => {
      const animationTriggerCss = `
        ${iframe.style.cssText}
        transition: transform ${this.ANIMATION_DURATION} ${this.ANIMATION_EASING}, opacity ${this.ANIMATION_DURATION} ${this.ANIMATION_EASING};
        transform: scale3d(1, 1, 1);
        opacity: 1;
      `;
      iframe.style.cssText = animationTriggerCss;

      // Clean up animation properties after completion
      setTimeout(() => {
        const cleanupCss = `
          ${iframe.style.cssText}
          transition: ;
          will-change: auto;
        `;
        iframe.style.cssText = cleanupCss;
      }, 450); // ANIMATION_DURATION + 50ms buffer
    });
  }
}

export const preloadService = new PreloadService();
